import { useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Paths that intentionally omit a trailing slash (auth, hub, checkout).
 * Amplify/_redirects already 301 some of these; keep the SPA consistent.
 */
const STRIP_SLASH_PREFIXES = [
  '/hub',
  '/account',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/confirm-email',
  '/join',
  '/pro/onboard',
  '/vendor/onboard',
  '/connect',
  '/cart',
  '/checkout',
  '/api',
  '/dashboard',
] as const;

function stripTrailingSlashes(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

function shouldStripTrailingSlash(pathname: string): boolean {
  const base = stripTrailingSlashes(pathname);
  return STRIP_SLASH_PREFIXES.some((prefix) => base === prefix || base.startsWith(`${prefix}/`));
}

/**
 * Align SPA URLs with hosting + sitemap:
 * - Public indexable paths → trailing slash (S3/Amplify 301 to `/path/`; do not strip back).
 * - Auth/hub/checkout → no trailing slash.
 * Collapses accidental `//` that break history.replaceState.
 */
export function NormalizeTrailingSlash() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    const cleaned = pathname.replace(/^\/\/+/, '/');
    const base = stripTrailingSlashes(cleaned);

    let nextPath: string;
    if (base === '/') {
      nextPath = '/';
    } else if (shouldStripTrailingSlash(cleaned)) {
      nextPath = base;
    } else {
      nextPath = `${base}/`;
    }

    if (nextPath === pathname) return;
    navigate(`${nextPath}${search}${hash}`, { replace: true });
  }, [pathname, search, hash, navigate]);

  return null;
}
